import { Dispatch, SetStateAction, useState } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Box, Button } from '@mui/material';
import {
  FieldArray,
  FieldArrayRenderProps,
  FormikProvider,
  useFormik,
} from 'formik';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { If } from 'src/components/If';
import { updateProfessional } from 'src/services/professional';
import { setBannerMessage } from 'src/store/display';
import { Professional } from 'src/types/professional';
import * as Yup from 'yup';

import CertificationForm from './CertificationForm';
import CertificationsList from './CertificationsList';

function Certifications({
  openForm,
  setOpenForm,
  professional,
  professionalRefetch,
}: {
  openForm: boolean;
  setOpenForm: Dispatch<SetStateAction<boolean>>;
  professional: Professional | undefined;
  professionalRefetch: () => void;
}) {
  const dispatch = useDispatch();
  const [index, setIndex] = useState<number | undefined>();
  const [actionType, setActionType] = useState<
    'create' | 'update' | 'delete'
  >();

  const FORM_VALIDATION = Yup.object().shape({
    certifications: Yup.array(
      Yup.object({
        name: Yup.string().required('Campo obrigatório'),
        issuer: Yup.string().required('Campo obrigatório'),
        issuedAt: Yup.string().required('Campo obrigatório').nullable(),
      }),
    ),
  });

  const formik = useFormik({
    initialValues: {
      certifications: professional?.certifications ?? [],
    },
    validationSchema: FORM_VALIDATION,
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: () => {},
  });

  const handleAddCertification = (arrayHelpers: FieldArrayRenderProps) => {
    arrayHelpers.push({
      name: '',
      issuer: '',
      issuedAt: '',
    });
    setIndex(undefined);
    setOpenForm(true);
    setActionType('create');
  };

  const updateProfessionalMutation = useMutation(updateProfessional, {
    onError: (error) => {
      if (error) {
        dispatch(
          setBannerMessage({
            autoclose: true,
            type: 'error',
            message: `Não foi possível ${
              actionType === 'update'
                ? 'editar'
                : actionType === 'delete'
                  ? 'remover'
                  : 'adicionar'
            } a certificação.`,
          }),
        );
      }
    },
    onSuccess: (result) => {
      if (result) {
        dispatch(
          setBannerMessage({
            autoclose: true,
            type: 'success',
            message: `Certificação ${
              actionType === 'update'
                ? 'atualizada'
                : actionType === 'delete'
                  ? 'removida'
                  : 'adicionada'
            } com sucesso.`,
          }),
        );
      }
      setOpenForm(false);
      setActionType(undefined);
      professionalRefetch();
    },
  });

  const { isLoading } = updateProfessionalMutation;

  const handleUpdate = (values: any) => {
    if (professional?.id) {
      updateProfessionalMutation.mutateAsync({
        id: professional.id,
        values: {
          data: {
            certifications: values,
          },
        },
      });
    }
  };

  return (
    <FormikProvider value={formik}>
      <FieldArray
        name="certifications"
        render={(arrayHelpers) => (
          <Box sx={{ padding: '32px 24px 24px' }}>
            <If condition={!openForm}>
              <Button
                startIcon={<AddCircleIcon color="primary" fontSize="large" />}
                color="secondary"
                onClick={() => handleAddCertification(arrayHelpers)}
                sx={{
                  textTransform: 'none',
                  letterSpacing: 'unset',
                  textDecoration: 'underline !important',
                  fontSize: '16px',
                  fontWeight: 700,
                  padding: '16px 8px',
                  color: '#FF8211',
                }}>
                Adicionar certificação
              </Button>
              <If condition={formik.values.certifications?.length > 0}>
                <CertificationsList
                  rows={formik.values.certifications}
                  setIndex={setIndex}
                  setOpen={setOpenForm}
                  setActionType={setActionType}
                  index={index}
                  arrayHelpers={arrayHelpers}
                  handleUpdate={handleUpdate}
                />
              </If>
            </If>
            <If condition={openForm}>
              <CertificationForm
                open={openForm}
                onClose={() => setOpenForm(false)}
                formik={formik}
                index={index ?? formik.values.certifications.length - 1}
                arrayHelpers={arrayHelpers}
                actionType={actionType}
                handleUpdate={handleUpdate}
              />
            </If>
          </Box>
        )}
      />
    </FormikProvider>
  );
}

export default Certifications;
